import * as z from "zod";
import {
  Field,
  FieldContent,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Controller, type UseFormReturn } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAboutIcons } from "../../hooks/useAboutIcons";
import { ArrowLeft, Loader, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { aboutFormSchema } from "./schemas";
import type { ApiAbout } from "../../types";
import RichTextEditor from "@/components/ui/rich-text-editor";
import { useNavigate } from "react-router-dom";
import BottomFixed from "@/components/bottom-fixed";
import { Switch } from "@/components/ui/switch";

interface AboutFormProps {
  form: UseFormReturn<z.infer<typeof aboutFormSchema>>;
  onSubmit: (data: z.infer<typeof aboutFormSchema>) => void;
  isLoading: boolean;
  about?: ApiAbout;
}

const AboutForm = ({ form, onSubmit, isLoading, about }: AboutFormProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { icons, isLoading: iconsLoading } = useAboutIcons();

  return (
    <div className="p-4 mb-24 rounded-md border w-full flex flex-col gap-y-4 bg-white">
      <form id="about-form" onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup className="gap-y-5 grid grid-cols-2 w-full">
          <div className="col-span-2 flex md:flex-row flex-col gap-5">
            <Controller
              name="title"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid} className="flex-1">
                  <FieldLabel htmlFor="title-input">
                    {t("modules.about.keys.title")}{" "}
                    <span className="text-destructive">*</span>
                  </FieldLabel>
                  <Input
                    {...field}
                    id="title-input"
                    aria-invalid={fieldState.invalid}
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            <Controller
              name="description"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field className="flex-1" data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="description-input">
                    {t("modules.about.keys.description")}{" "}
                    <span className="text-destructive">*</span>
                  </FieldLabel>
                  <Input
                    {...field}
                    id="description-input"
                    aria-invalid={fieldState.invalid}
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            <Controller
              name="icon_name"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid} className="flex-1">
                  <FieldLabel htmlFor="icon-name-input">
                    {t("modules.about.keys.icon_name")}{" "}
                    <span className="text-destructive">*</span>
                  </FieldLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={iconsLoading}
                  >
                    <SelectTrigger
                      id="icon-name-input"
                      aria-invalid={fieldState.invalid}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {icons
                        .filter((i) => i.value !== "documentation")
                        .map((icon) => (
                          <SelectItem
                            key={icon.value}
                            value={icon.value}
                            disabled={
                              !icon.is_available && icon.value !== "other"
                            }
                          >
                            {icon.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
          </div>

          <Controller
            name="body_wysiwyg"
            control={form.control}
            render={({ field, fieldState }) => {
              const handleBodyChange = (html: string) => {
                field.onChange(html);
                const doc = new DOMParser().parseFromString(html, "text/html");
                form.setValue("body_simple", doc.body.textContent ?? "", {
                  shouldValidate: true,
                });
              };
              return (
                <Field className="col-span-2" data-invalid={fieldState.invalid}>
                  <FieldLabel>
                    {t("modules.about.keys.body_wysiwyg")}{" "}
                    <span className="text-destructive">*</span>
                  </FieldLabel>
                  <RichTextEditor
                    value={field.value}
                    onChange={handleBodyChange}
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              );
            }}
          />
          <div className="col-span-2 flex sm:flex-row flex-col gap-5">
            <Controller
              name="is_active"
              control={form.control}
              render={({ field }) => (
                <Field orientation="vertical" className="flex-1">
                  <FieldContent>
                    <FieldLabel htmlFor="is-active-input">
                      {t("modules.about.keys.is_active")}
                    </FieldLabel>
                  </FieldContent>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    id="is-active-input"
                  />
                </Field>
              )}
            />
          </div>
        </FieldGroup>
      </form>
      <BottomFixed className="bg-white border-t border-l">
        <div className="flex flex-wrap flex-row items-center sm:justify-end gap-2">
          {about && (
            <Button
              className="flex-1 sm:flex-none"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft />
              {t("keys.cancel")}
            </Button>
          )}
          <Button
            className="flex-1 sm:flex-none"
            disabled={isLoading}
            type="submit"
            form="about-form"
          >
            {isLoading ? <Loader className="animate-spin" /> : <Save />}
            {t("keys.save")}
          </Button>
        </div>
      </BottomFixed>
    </div>
  );
};

export default AboutForm;
